import {Text} from "@gs-ux-uitoolkit-react/text";
import {Form, FormFeedback, FormControl} from "@gs-ux-uitoolkit-react/form";
import {Input} from "@gs-ux-uitoolkit-react/input";
import {Button} from "@gs-ux-uitoolkit-react/button";
import {Alert} from '@gs-ux-uitoolkit-react/alert';
import React, {useState} from "react";
import axios, {AxiosError} from "axios";
import {ApiErrorResponse} from "@gsas/gsas-sdk";
import {useSignInContext} from "../context/SignInContextProvider";

interface UsernameFormProps {
    onContinue: (username: string) => void;
    disabled?: boolean;
}


function isLowerEnvironment(): boolean {
    const domain = window.location.hostname;
    return domain.includes('dev.') || domain.includes('localhost') || domain.includes('qa.') || domain.includes('uat.') || domain.includes('uataws.') ;
}

// same landing page /sign-in redirects to when no sign-in flow is in progress
function defaultLandingPath(): string {
    return window.location.hostname.includes('custodysolutions') ? '/client-app' : '/app';
}

export function UsernameForm({onContinue, disabled = false}: UsernameFormProps) {
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState<string>();
    const {checkInIAMHub} = useSignInContext();

    const handleContinue = async () => {
        const trimmed = username.trim().replace(/[&\/\\#+()$~%'":*?<>{}]/g, '').toLowerCase();
        if (!trimmed) {
            setError('Username is required');
            return;
        }
        let result;
        try {
            result = await checkInIAMHub(trimmed);
        } catch (e: unknown) {
            console.log(e);
            const response = axios.isAxiosError(e)
                ? (e as AxiosError<ApiErrorResponse>).response
                : undefined;
            if (response && response.status >= 400 && response.status < 500) {
                const validationError = response.data?.validationErrors?.[0];
                const rawMessage = validationError?.displayMessage || response.data?.displayMessage;
                // "loginId" is the technical path-parameter name shared across many backend
                // endpoints; this sign-in screen calls it "Username" everywhere else, so swap the
                // word for the term our users actually recognise rather than relabeling it
                // server-side for every other caller of that shared validator.
                const displayMessage = validationError?.field === 'loginId'
                    ? rawMessage?.replace(/^loginId\b/i, 'Username')
                    : rawMessage;
                setMessage(displayMessage || 'Please enter a valid username');
            } else {
                setMessage('The server is unavailable right now, please try again later');
            }
            return;
        }
        if (result.data.isUserMigratedStatus === 2 && result.data.enableIamHubMigrationFlow === true) {
            if (isLowerEnvironment()) {
                var pingFederateBaseUrl = 'https://idfs-qa.gs.com';
                var clientId = '4b1a409291684b609631b85d5e04c24d';
                const domain = window.location.hostname;
                var redirectUri = 'https://dev.advisorsolutions.site.gs.com/ping-callback';
                if (domain.includes('dev.custodysolutions')) {
                    redirectUri = 'https://dev.custodysolutions.site.gs.com/ping-callback';
                } else if (domain.includes('qa.custodysolutions')) {
                    redirectUri = 'https://qa.custodysolutions.site.gs.com/ping-callback';
                } else if (domain.includes('uat.custodysolutions')){
                    redirectUri = 'https://uat.custodysolutions.gs.com/ping-callback';
                    clientId = '0811b55f0a0547a98d7f5a6d54d26ff0';
                } else if (domain.includes('dev.advisorsolutions')) {
                    redirectUri = 'https://dev.advisorsolutions.site.gs.com/ping-callback';
                } else if (domain.includes('qa.advisorsolutions')) {
                    redirectUri = 'https://qa.advisorsolutions.site.gs.com/ping-callback';
                } else if (domain.includes('uat.advisorsolutions')){
                    redirectUri = 'https://uat.advisorsolutions.gs.com/ping-callback';
                    clientId = '0811b55f0a0547a98d7f5a6d54d26ff0';
                }
            } else {
                var pingFederateBaseUrl = 'https://idfs.gs.com';
                var clientId = 'a31bb4741fdf42f79ea106db8b3b5c83';
                const domain = window.location.hostname;
                var redirectUri = 'https://advisorsolutions.gs.com/ping-callback';
                if(domain.includes('custodysolutions')) {
                    redirectUri = 'https://custodysolutions.gs.com/ping-callback';
                }
            }
            // Always send a destination: without state the gateway falls back to /ping-callback itself
            // (blank screen), e.g. when the user started from a bookmarked ?resume= URL with no next.
            const destination = new URLSearchParams(window.location.search).get('next') || defaultLandingPath();
            const stateBase64 = encodeURIComponent(btoa(JSON.stringify({ redirectUri: destination, clientId })));
            const pingFederateUrl = `${pingFederateBaseUrl}/as/authorization.oauth2?response_type=code&scope=openid&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&login_hint=${encodeURIComponent(result.data.iamLoginId)}&state=${stateBase64}`;
            window.location.href = pingFederateUrl;
        } else if (result.data.enableIamHubMigrationFlow === true) {
            //user partially migrated or not migrated at all, continue to password screen
            onContinue(trimmed);
        } else {
            //temporarily allow all users to continue regardless of result to avoid blocking users
            onContinue(trimmed);
        }
    };

    return (
        <div className="flex flex-col items-stretch gap-6 w-full">
            <Text typography="textTitle02" data-qa-comp="sign-in-title">Sign in</Text>
            { message &&
                <Alert status="warning">
                    {message}
                </Alert>
            }
            <Form className="flex flex-col gap-3 w-full" onSubmit={e => { e.preventDefault(); handleContinue(); }}>
                <FormControl status={error ? 'error' : 'none'}>
                    <Input
                        autoFocus
                        name="username"
                        type="text"
                        size="md"
                        emphasis="regular"
                        label="Enter Username"
                        className="w-full"
                        disabled={disabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                        value={username}
                        data-qa-comp="username-input"
                    />
                    <FormFeedback>{error}</FormFeedback>
                </FormControl>
            </Form>
            <Button
                className="w-full"
                size="md"
                appearance="highlight"
                emphasis="bold"
                type="submit"
                onClick={handleContinue}
                disabled={disabled}
                data-qa-comp="continue-button"
            >
                Next
            </Button>
        </div>
    );
}
